import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import FoundationsLayout from './pages/FoundationsLayout';
import ComponentsLayout from './pages/ComponentsLayout';
import PatternsLayout from './pages/PatternsLayout';
import ResourcesLayout from './pages/ResourcesLayout';

// Foundation pages
import ColorsPage from './pages/foundations/ColorsPage';
import ColorsImplementation from './pages/foundations/ColorsImplementation';
import ColorsUsageGuidelines from './pages/foundations/ColorsUsageGuidelines';
import TypographyPage from './pages/foundations/TypographyPage';
import SpacingPage from './pages/foundations/SpacingPage';
import BordersPage from './pages/foundations/BordersPage';
import ShadowsPage from './pages/foundations/ShadowsPage';

// Component pages
import ButtonPage from './pages/components/ButtonPage';
import ButtonEventHandling from './pages/components/ButtonEventHandling';
import ButtonAPIReference from './pages/components/ButtonAPIReference';
import PanelPage from './pages/components/PanelPage';
import InputPage from './pages/components/InputPage';
import CheckboxPage from './pages/components/CheckboxPage';
import DialogPage from './pages/components/DialogPage';
import TooltipPage from './pages/components/TooltipPage';
import ScrollbarPage from './pages/components/ScrollbarPage';
import ProgressBarPage from './pages/components/ProgressBarPage';
import InventorySlotPage from './pages/components/InventorySlotPage';

// Pattern pages
import NavigationPage from './pages/patterns/NavigationPage';
import NavigationTabs from './pages/patterns/NavigationTabs';
import NavigationBreadcrumbs from './pages/patterns/NavigationBreadcrumbs';
import FormsPage from './pages/patterns/FormsPage';
import DialogsPage from './pages/patterns/DialogsPage';
import InventoryPage from './pages/patterns/InventoryPage';
import ChatPage from './pages/patterns/ChatPage';

// Resource pages
import DownloadsPage from './pages/resources/DownloadsPage';
import IconsPage from './pages/resources/IconsPage';
import FigmaPage from './pages/resources/FigmaPage';
import FontsPage from './pages/resources/FontsPage';
import ImplementationPage from './pages/resources/ImplementationPage';

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center p-8">
          <h1 className="text-2xl text-osrs-red font-bold mb-4">Something went wrong</h1>
          <p className="text-osrs-yellow mb-4">Please try refreshing the page</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-osrs-gold text-osrs-black px-4 py-2 rounded"
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            
            {/* Foundations routes */}
            <Route path="foundations" element={<FoundationsLayout />}>
              <Route index element={<Navigate to="colors" replace />} />
              <Route path="colors" element={<ColorsPage />} />
              <Route path="colors/implementation" element={<ColorsImplementation />} />
              <Route path="colors/usage-guidelines" element={<ColorsUsageGuidelines />} />
              <Route path="typography" element={<TypographyPage />} />
              <Route path="spacing" element={<SpacingPage />} />
              <Route path="borders" element={<BordersPage />} />
              <Route path="shadows" element={<ShadowsPage />} />
            </Route>
            
            {/* Components routes */}
            <Route path="components" element={<ComponentsLayout />}>
              <Route index element={<Navigate to="button" replace />} />
              <Route path="button" element={<ButtonPage />} />
              <Route path="button/event-handling" element={<ButtonEventHandling />} />
              <Route path="button/api-reference" element={<ButtonAPIReference />} />
              <Route path="panel" element={<PanelPage />} />
              <Route path="input" element={<InputPage />} />
              <Route path="checkbox" element={<CheckboxPage />} />
              <Route path="dialog" element={<DialogPage />} />
              <Route path="tooltip" element={<TooltipPage />} />
              <Route path="scrollbar" element={<ScrollbarPage />} />
              <Route path="progress-bar" element={<ProgressBarPage />} />
              <Route path="inventory-slot" element={<InventorySlotPage />} />
            </Route>
            
            {/* Patterns routes */}
            <Route path="patterns" element={<PatternsLayout />}>
              <Route index element={<Navigate to="navigation" replace />} />
              <Route path="navigation" element={<NavigationPage />} />
              <Route path="navigation/tabs" element={<NavigationTabs />} />
              <Route path="navigation/breadcrumbs" element={<NavigationBreadcrumbs />} />
              <Route path="forms" element={<FormsPage />} />
              <Route path="dialogs" element={<DialogsPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="chat" element={<ChatPage />} />
            </Route>
            
            {/* Resources routes */}
            <Route path="resources" element={<ResourcesLayout />}>
              <Route index element={<Navigate to="downloads" replace />} />
              <Route path="downloads" element={<DownloadsPage />} />
              <Route path="icons" element={<IconsPage />} />
              <Route path="figma" element={<FigmaPage />} />
              <Route path="fonts" element={<FontsPage />} />
              <Route path="implementation" element={<ImplementationPage />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
